

































import Vue, { PropType } from 'vue';
import { AddressFormData, CardItem } from '../types';
import AddressForm from './AddressForm.vue';
import BaseCard from './BaseCard.vue';
import BillingModal from './BillingModal.vue';
import Button from '~/components/builders/Base/Button.vue';
import { dateToStringRaw } from '~/utils/dateToString';
import BillingService, {
    BillingAccount,
    CustomerAddress,
    Invoice,
    Subscription,
} from '~/services/BillingService';
import countries from 'highered-utils/data/countries.json';

export default Vue.extend({
    name: 'BillingDetailsCard',
    components: {
        AddressForm,
        BaseCard,
        BillingModal,
        Button,
    },
    props: {
        subscription: {
            type: Object as PropType<Subscription>,
            required: true,
        },
    },
    data() {
        return {
            loading: true,
            error: null as string | null,

            modalOpen: false,
            invoice: null as Invoice | null,
            billingAccount: null as BillingAccount | null,

            // Details form
            addressForm: null as AddressFormData | null,
            shippingSameAsAddress: true,
            countries: countries,
        };
    },
    computed: {
        billingDetailItems(): CardItem[] {
            const items: CardItem[] = [];
            if (!this.invoice) return items;

            if (this.invoice.customerName)
                items.push({
                    label: this.$t('plansAndBilling.billingDetails.name').toString(),
                    value: this.invoice.customerName,
                });

            if (!this.invoice.customerAddress) return items;

            const { customerAddress: address } = this.invoice;
            const join = (...args: (string | null)[]) => args.filter(v => v).join(', ');

            if (!!address.line1 || !!address.line2) {
                const streetAddress = join(address.line1, address.line2);
                items.push({
                    label: this.$t('plansAndBilling.billingDetails.streetAddress').toString(),
                    value: streetAddress,
                });
            }

            if (!!address.country || !!address.state || !!address.city || !!address.postalCode) {
                const postal = join(
                    address.country,
                    address.city,
                    address.state,
                    address.postalCode
                );
                items.push({
                    label: this.$t(
                        'plansAndBilling.billingDetails.postalCodeCityCountry'
                    ).toString(),
                    value: postal,
                });
            }

            return items;
        },
        billingDetailsSubHeader(): string | null {
            if (this.subscription.cancelAtPeriodEnd) return null;

            const nextInvoice = dateToStringRaw(this.subscription.expiresAt) || '';
            return `${this.$t('plansAndBilling.billingDetails.yourNextInvoice')}: ${nextInvoice}`;
        },
    },
    methods: {
        getCountry(account: BillingAccount | null, address?: CustomerAddress) {
            if (!!this.addressForm) return this.addressForm.country;

            const first = this.countries[0];
            if (!account || !account.address) return first;

            if (!address) address = account.address;
            if (!address.country) return first;

            const found = this.countries.find(
                country => country.label.toLowerCase() === address!.country.toLowerCase()
            );
            return found || first;
        },

        handleOpen() {
            this.modalOpen = true;
        },
        handleClose() {
            this.modalOpen = false;
        },
        handleSaveBillingDetails() {
            if (!this.billingAccount || !this.addressForm) return;

            const { shipping, ...address } = this.addressForm;
            if (!shipping) return;

            const updateBillingAccountError = this.$t(
                'plansAndBilling.billingDetails.errors.updateBillingAccount'
            ).toString();

            const params: BillingAccount = {
                ...this.billingAccount,
                address: {
                    ...address,
                    country: address.country.label,
                },
                shipping: {
                    ...shipping,
                    address: {
                        ...shipping.address,
                        country: shipping.address.country.label,
                    },
                },
            };
            this.loading = true;
            BillingService.updateBillingAccount(params)
                .then(ba => {
                    this.billingAccount = ba;
                    this.initAddressForm(ba);

                    return BillingService.getNextInvoice(this.subscription.id).then(
                        invoice => (this.invoice = invoice)
                    );
                })
                .catch(() => (this.error = updateBillingAccountError))
                .finally(() => {
                    this.loading = false;
                    this.handleClose();
                });
        },
        handleResetShipping() {
            if (!this.billingAccount || !this.addressForm) return;

            const name = this.addressForm.shipping ? this.addressForm.shipping.name : '';
            const phone = this.addressForm.shipping ? this.addressForm.shipping.phone : '';

            this.addressForm.shipping = {
                name,
                phone,
                address: {
                    country: this.addressForm.country,
                    state: this.addressForm.state,
                    postalCode: this.addressForm.postalCode,
                    city: this.addressForm.city,
                    line1: this.addressForm.line1,
                    line2: this.addressForm.line2,
                },
            };
        },

        initAddressForm(account: BillingAccount | null) {
            const country = this.getCountry(account);

            let form: AddressFormData | null;
            if (!account || !account.address) {
                form = {
                    country,
                    state: '',
                    city: '',
                    postalCode: '',
                    line1: '',
                    line2: '',
                };
            } else {
                const { address } = account;
                form = {
                    country,
                    state: address.state || '',
                    city: address.city || '',
                    postalCode: address.postalCode || '',
                    line1: address.line1 || '',
                    line2: address.line2 || '',
                };
            }

            if (!account || !account.shipping) {
                form.shipping = {
                    name: '',
                    phone: '',
                    address: {
                        country,
                        state: '',
                        city: '',
                        postalCode: '',
                        line1: '',
                        line2: '',
                    },
                };
            } else {
                const { shipping } = account;
                const { address } = shipping;
                const country = this.getCountry(account, address);

                form.shipping = {
                    name: shipping.name,
                    phone: shipping.phone || '',
                    address: {
                        country,
                        state: address.state || '',
                        city: address.city || '',
                        postalCode: address.postalCode || '',
                        line1: address.line1 || '',
                        line2: address.line2 || '',
                    },
                };
            }

            this.addressForm = form;
        },
    },
    mounted() {
        const loadBillingAccountError = this.$t(
            'plansAndBilling.billingDetails.errors.loadBillingAccount'
        ).toString();

        Promise.all([
            BillingService.getNextInvoice(this.subscription.id)
                .then(invoice => (this.invoice = invoice))
                .catch(() => (this.error = null)),

            BillingService.getDefaultBillingAccount()
                .then(ba => (this.billingAccount = ba))
                .catch(() => (this.error = loadBillingAccountError))
                .finally(() => this.initAddressForm(this.billingAccount)),
        ]).finally(() => (this.loading = false));
    },
});
